import styled from 'styled-components'
import PropTypes from 'prop-types'
import { t } from 'ttag'
import { useCallback, useContext } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { features } from '@h2/components/media'
import { Globals } from '@h2/globals'
import { vagRundFont } from '@h2/components/mixins'
import { PG, SLB, NAVIANCEBLUE } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Box as BoxBase } from '@h2/components/layout/box'
import { Heading as HeadingBase } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { CelebrationIcon } from '@h2/components/icons/celebration'
import { Card } from '@h2/common/components/card'
import { Link as LinkBase } from '@h2/common/components/link'
import { userCompletedDialogRegistration } from '@h2/common/actions/userCompletedDialogRegistration'
import { closeDialog } from '@main/actions/signinOrRegisterDialog'
import { sessionStarted } from '@h2/features/auth'

import { Create } from '../highlights/create'

const Heading = styled(HeadingBase)`
  display: flex;
  flex-direction: column;
  margin: 0;
  font-size: 24px;
  line-height: 28px;

  > span[role='img'] {
    margin-bottom: 15px;

    svg {
      width: auto;
      height: 130px;
    }
  }

  @media (${features.minWidth.laptop}) {
    font-size: 30px;
    line-height: 34px;
  }
`
const Wrap = styled(Flex)`
  height: 100%;
  flex-direction: column;
`
const Box = styled(BoxBase)`
  @media (${features.minWidth.laptop}) {
    display: block;
  }
`
const Link = styled(LinkBase)`
  ${vagRundFont};
  padding: 10px 18px;
  color: white;
`
const TextBox = styled(Text)`
  margin-bottom: 35px;
`
const Finished = ({ inDialog, properties }) => {
  const dispatch = useDispatch()
  const { isNavianceUser, backToNavianceURL, postSigninURI } = useContext(Globals)
  const { state: previousLocation, pathname } = useLocation()
  const inRegistration = !isNavianceUser
    ? Boolean(properties?.name)
    : previousLocation?.panel === 'register'
  const onClickFinished = useCallback(
    evt => {
      evt.preventDefault()

      if (isNavianceUser) {
        dispatch(closeDialog())
        window.location.replace(backToNavianceURL)
      } else {
        dispatch(
          sessionStarted({
            inDialog,
            postSigninURI,
            pathname,
            onNoRedirect: () => dispatch(userCompletedDialogRegistration())
          })
        )
      }
    },
    [inDialog, dispatch, pathname, postSigninURI, isNavianceUser, backToNavianceURL]
  )
  const text = isNavianceUser
    ? ''
    : !properties.isNavigator
    ? t`Your account has been successfully created, and you can start using the CareerExplorer to explorer your career opportunities.`
    : t`Your account has been successfully created, and you can start using the CareerExplorer to help your Explorers reach their career goals.`
  const linkText = isNavianceUser
    ? ''
    : properties.isNavigator
    ? t`Start Navigating`
    : t`Start Exploring`
  const headingText = !isNavianceUser
    ? t`Congratulations, you're all done!`
    : t`Congratulations!`
  const bodyText = !isNavianceUser
    ? text
    : inRegistration
    ? t`Your account is created and linked to Naviance.`
    : t`You have linked your Headed2 and Naviance accounts.`

  return (
    <Card inDialog={inDialog}>
      <Card.Highlight
        color={isNavianceUser ? NAVIANCEBLUE : properties.isNavigator ? SLB : PG}>
        <Create
          isNamePanel={isNavianceUser}
          type={
            isNavianceUser
              ? 'navianceUser'
              : properties.isNavigator
              ? 'navigator'
              : 'explorer'
          }
          inRegistration={inRegistration}
        />
      </Card.Highlight>
      <Card.Content>
        <Wrap>
          <Heading level="2" align="center">
            <CelebrationIcon size="large" />
            <span>{headingText}</span>
          </Heading>
          <TextBox align="center" size="large" my="20px">
            {bodyText}
          </TextBox>
          <Box width="250px" alignSelf="center">
            <Link isFullWidth variant="button" fontSize="20px" onClick={onClickFinished}>
              {!isNavianceUser ? linkText : t`Return to Naviance`}
            </Link>
          </Box>
        </Wrap>
      </Card.Content>
      {!isNavianceUser && (
        <Card.Detour>
          <Link isFullWidth variant="button" fontSize="20px" onClick={onClickFinished}>
            {linkText}
          </Link>
        </Card.Detour>
      )}
    </Card>
  )
}

Finished.propTypes = {
  inDialog: PropTypes.bool,
  properties: PropTypes.object
}

export { Finished }
